import type { LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@scio3/ui';
import { cn } from '@/lib/utils';

interface ViewToggleOption {
  value: string;
  icon: LucideIcon;
  label: string;
  onMouseEnter?: () => void;
}

interface ViewToggleProps {
  value: string;
  onValueChange: (value: string) => void;
  options: ViewToggleOption[];
  className?: string;
  'data-testid'?: string;
}

/**
 * Segmented toggle for switching between view modes (grid/list/map).
 * Renders as a pill container with a sliding active indicator.
 *
 * Each segment is icon-only, so `option.label` serves as both the accessible name and a tooltip.
 */
export function ViewToggle({
  value,
  onValueChange,
  options,
  className,
  'data-testid': testId,
}: ViewToggleProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn('inline-flex items-center gap-0.5 rounded-md bg-border p-1', className)}
        role="radiogroup"
        data-testid={testId}
      >
        {options.map((option) => {
          const Icon = option.icon;
          const isActive = value === option.value;
          return (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-label={option.label}
                  data-testid={testId ? `${testId}-${option.value}` : undefined}
                  className={cn(
                    'inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded transition-colors',
                    isActive
                      ? 'bg-card text-foreground'
                      : // Solid muted, not an alpha-faded foreground — faded icons read as disabled.
                        'text-muted-foreground hover:bg-foreground/10 hover:text-foreground',
                  )}
                  onClick={() => onValueChange(option.value)}
                  onMouseEnter={option.onMouseEnter}
                >
                  <Icon className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{option.label}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
